import { useState } from "react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { errMsg, fmtDate, inr } from "@/lib/api";
import { useAdminApi } from "@/lib/admin";
import { useLoad, PageHead, DataTable, Td, NativeSelect } from "@/components/admin/kit";

const STATUS = ["pending", "approved", "rejected", "suspended"];
const ACTIONS = { pending: ["approve", "reject"], approved: ["suspend"], suspended: ["activate"], rejected: [] };

const Detail = ({ id, onClose, isAdmin, onChange }) => {
  const call = useAdminApi();
  const [d, reload] = useLoad(`/admin/mlm/distributors/${id}`);
  const [note, setNote] = useState("");
  const [sponsor, setSponsor] = useState("");
  const act = async (action) => {
    try {
      await call("patch", `/admin/mlm/distributors/${id}/status`, { action, note });
      toast.success(`Distributor ${{ approve: "approved", reject: "rejected", suspend: "suspended", activate: "activated" }[action] || action}`);
      setNote("");
      reload();
      onChange();
    } catch (e) {
      toast.error(errMsg(e));
    }
  };
  const changeSponsor = async () => {
    try {
      await call("patch", `/admin/mlm/distributors/${id}/sponsor`, { sponsor_code: sponsor });
      toast.success("Sponsor updated");
      reload();
      onChange();
    } catch (e) {
      toast.error(errMsg(e));
    }
  };
  const openDoc = async (doc) => {
    const win = window.open("", "_blank");
    try {
      const blob = await call("get", `/admin/mlm/distributors/${id}/documents/${doc.id}`, undefined, { responseType: "blob" });
      const url = URL.createObjectURL(blob);
      if (win) win.location.href = url;
      setTimeout(() => URL.revokeObjectURL(url), 60000);
    } catch (e) {
      if (win) win.close();
      toast.error(errMsg(e));
    }
  };
  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="max-h-[90vh] max-w-2xl overflow-y-auto" data-testid="distributor-detail">
        <DialogHeader><DialogTitle>{d ? `${d.full_name} · ${d.distributor_id || "No ID yet"}` : "Loading…"}</DialogTitle></DialogHeader>
        {d && (
          <div className="space-y-4 text-sm">
            <dl className="grid gap-2 sm:grid-cols-2">
              {[["Status", d.status], ["Email", `${d.email} (verified)`], ["Mobile", d.mobile], ["Address", `${d.address}, ${d.district}, ${d.state} ${d.pincode}`], ["Sponsor", d.sponsor ? `${d.sponsor.full_name} (${d.sponsor.distributor_id})` : "None"], ["Package interest", d.package_interest ? `${d.package_interest.name} ${inr(d.package_interest.price)}` : "None"], ["Directs / Team", `${d.directs} / ${d.team_size}`], ["Personal / Team sales", `${inr(d.personal_sales.amount)} / ${inr(d.team_sales.amount)}`], ["Registered", fmtDate(d.registered_at, true)], ["Login account", d.account_email]].map(([k, v]) => <div key={k}><dt className="text-xs text-slate-500">{k}</dt><dd className="break-words font-medium capitalize-first">{v}</dd></div>)}
            </dl>
            <div className="flex flex-wrap gap-2">{d.documents.map((doc) => <Button key={doc.id} size="sm" variant="outline" onClick={() => openDoc(doc)} data-testid={`dist-doc-${doc.doc_type}`}>View {doc.doc_type.replace("_", " ")}</Button>)}</div>
            {isAdmin && ACTIONS[d.status].length > 0 && (
              <div className="space-y-2 rounded-xl bg-slate-50 p-3">
                <Input placeholder="Note / reason (optional)" value={note} onChange={(e) => setNote(e.target.value)} data-testid="dist-action-note" />
                <div className="flex gap-2">{ACTIONS[d.status].map((a) => <Button key={a} size="sm" variant={a === "approve" || a === "activate" ? "default" : "destructive"} className="capitalize" onClick={() => act(a)} data-testid={`dist-action-${a}`}>{a}</Button>)}</div>
              </div>
            )}
            {isAdmin && d.status === "pending" && (
              <div className="flex gap-2"><Input placeholder="New sponsor ID (blank = none)" value={sponsor} onChange={(e) => setSponsor(e.target.value.toUpperCase())} data-testid="dist-sponsor-input" /><Button size="sm" variant="outline" onClick={changeSponsor} data-testid="dist-sponsor-save">Change sponsor</Button></div>
            )}
            <div><p className="mb-1 text-xs font-semibold uppercase text-slate-500">History</p>{d.history.map((h, i) => <p key={i} className="text-xs">{fmtDate(h.at, true)} · {h.status} · {h.by}{h.note && ` · ${h.note}`}</p>)}</div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default function Distributors({ isAdmin }) {
  const [status, setStatus] = useState("");
  const [q, setQ] = useState("");
  const [open, setOpen] = useState(null);
  const [stats, reloadStats] = useLoad("/admin/mlm/stats");
  const [rows, reload] = useLoad("/admin/mlm/distributors", { status: status || undefined, q: q || undefined });
  const cards = stats ? [["Total distributors", stats.total], ["Active", stats.active], ["Pending registrations", stats.pending], ["Suspended", stats.suspended], ["Distributor-linked sales", inr(stats.distributor_sales)], ["Package revenue", inr(stats.package_revenue)], ["Eligible commission", inr(stats.eligible_commission)], ["Pending withdrawals", stats.withdrawals_pending]] : [];
  return (
    <div>
      <PageHead title="Distributors">
        <Input placeholder="Search name, ID, email, mobile" value={q} onChange={(e) => setQ(e.target.value)} className="h-9 w-64" data-testid="dist-search" />
        <NativeSelect value={status} onChange={setStatus} options={STATUS} placeholder="All statuses" testid="dist-status-filter" />
      </PageHead>
      <div className="mb-6 grid gap-3 sm:grid-cols-2 xl:grid-cols-4">{cards.map(([k, v]) => <div key={k} className="rounded-2xl border border-slate-200 bg-white p-4" data-testid={`mlm-stat-${k.toLowerCase().replace(/\W+/g, "-")}`}><p className="text-xs font-semibold uppercase text-slate-500">{k}</p><p className="font-mono-num mt-1 text-xl font-bold">{v}</p></div>)}</div>
      <DataTable head={["Distributor", "ID", "Sponsor", "District", "Status", "Registered", ""]} testid="distributors-table">
        {(rows || []).map((d) => (
          <tr key={d.id}>
            <Td><p className="font-semibold">{d.full_name}</p><p className="text-xs text-slate-500">{d.email} · {d.mobile}</p></Td>
            <Td>{d.distributor_id || "—"}</Td><Td>{d.sponsor_code || "—"}</Td><Td>{d.district}</Td><Td className="capitalize">{d.status}</Td><Td>{fmtDate(d.registered_at)}</Td>
            <Td><Button size="sm" variant="outline" onClick={() => setOpen(d.id)} data-testid={`dist-open-${d.id}`}>Open</Button></Td>
          </tr>
        ))}
      </DataTable>
      {open && <Detail id={open} isAdmin={isAdmin} onClose={() => setOpen(null)} onChange={() => { reload(); reloadStats(); }} />}
    </div>
  );
}
